/**
 * useTestKitLogs — test-kit log state persisted on the Pi (namespace
 * `test-kit-logs`) via `useServerBackedState`. The legacy localStorage
 * copy (`reefapp.testKits.logs`) seeds the default so the user sees
 * their data instantly, even before the first Pi round-trip / one-time
 * migration; the legacy key is never deleted (offline fallback).
 * Also calls `writeTestKitLog` for audit-log mirroring on each add.
 */
import { useState, useCallback, useRef } from 'react';
import type { TestLogEntry } from '@reefapp/shared';
import { useServerBackedState } from '../hooks/useServerBackedState';
import { writeTestKitLog } from '../lib/test-kit-log';

/** Legacy browser-only key — read as the seed default, never deleted. */
const LEGACY_STORAGE_KEY = 'reefapp.testKits.logs';

/** `/api/userdata` namespace holding the full log array on the Pi. */
export const TEST_KIT_LOGS_NAMESPACE = 'test-kit-logs';

function readLegacyLogs(): TestLogEntry[] {
  try {
    const raw = window.localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed as TestLogEntry[];
  } catch {
    // Corrupt storage — start fresh
  }
  return [];
}

export interface UseTestKitLogsReturn {
  logs: TestLogEntry[];
  addLog(entry: Omit<TestLogEntry, 'id' | 'date'>): void;
  deleteLog(id: string): void;
  clearAllLogs(): void;
  setLogs(logs: TestLogEntry[]): void;
}

export function useTestKitLogs(): UseTestKitLogsReturn {
  const [legacyDefault] = useState(readLegacyLogs);
  const { value, setValue } = useServerBackedState<TestLogEntry[]>(
    TEST_KIT_LOGS_NAMESPACE,
    legacyDefault,
  );
  // Tolerate a malformed server blob — render nothing rather than crash.
  const logs = Array.isArray(value) ? value : [];
  const logsRef = useRef(logs);
  logsRef.current = logs;

  const addLog = useCallback(
    (entry: Omit<TestLogEntry, 'id' | 'date'>) => {
      const newEntry: TestLogEntry = {
        ...entry,
        id: crypto.randomUUID(),
        date: new Date().toISOString(),
      };

      setValue([newEntry, ...logsRef.current]);

      // Fire-and-forget audit log
      void writeTestKitLog({
        kitSlug: entry.kitSlug,
        brand: entry.brand,
        testName: entry.testName,
        parameterKey: entry.parameterKey,
        parameter: entry.parameter,
        result: entry.result,
        unit: entry.unit,
        notes: entry.notes,
      });
    },
    [setValue],
  );

  const deleteLog = useCallback(
    (id: string) => {
      setValue(logsRef.current.filter((l) => l.id !== id));
    },
    [setValue],
  );

  const clearAllLogs = useCallback(() => {
    setValue([]);
  }, [setValue]);

  const setLogs = useCallback(
    (newLogs: TestLogEntry[]) => {
      setValue(newLogs);
    },
    [setValue],
  );

  return { logs, addLog, deleteLog, clearAllLogs, setLogs };
}
